/**
 * Connections Route — source read-model list and desktop SourceDetail aside.
 */

import { useEffect, useMemo, useState } from 'react';
import { cn } from '@ownyou/ui-design-system';
import { SourceDetailPanel } from '../components/connections/SourceDetailPanel';
import { useSourceReadModels } from '../hooks/useSourceReadModels';
import { recordSlice05ConnectionMarker } from '../lib/source-read-model/instrumentation';
import type {
  SourceReadModel,
  SourceReadModelDisplayState,
} from '../lib/source-read-model/read-model';

function statusLabel(source: SourceReadModel): string {
  if (source.id === 'location') return 'Virtual location';
  if (!source.requiresSourceAuthOutcome) return 'Local permission';

  const labels: Record<SourceReadModelDisplayState, string> = {
    connected: 'Connected',
    needs_reauth: 'Needs ReAuth',
    disconnected: 'Disconnected',
    never_connected: 'Not connected',
    in_progress: 'In progress',
    problem: 'Problem',
  };
  return labels[source.displayState];
}

function actionCopy(source: SourceReadModel): string {
  if (source.id === 'location') return 'Local permission only';
  if (!source.requiresSourceAuthOutcome) return 'Review local permission';

  switch (source.displayState) {
    case 'connected':
      return 'Review source';
    case 'needs_reauth':
      return 'Reconnect required';
    case 'problem':
      return 'Review problem';
    case 'in_progress':
      return 'Connection in progress';
    case 'disconnected':
    case 'never_connected':
      return 'Set up connection';
  }
}

function stateTone(source: SourceReadModel): string {
  if (source.id === 'location' || !source.requiresSourceAuthOutcome) {
    return 'border-sky-200 bg-sky-50 text-sky-800';
  }

  switch (source.displayState) {
    case 'connected':
      return 'border-emerald-200 bg-emerald-50 text-emerald-800';
    case 'needs_reauth':
      return 'border-amber-200 bg-amber-50 text-amber-800';
    case 'problem':
      return 'border-red-200 bg-red-50 text-red-800';
    case 'in_progress':
      return 'border-blue-200 bg-blue-50 text-blue-800';
    case 'disconnected':
    case 'never_connected':
      return 'border-neutral-200 bg-neutral-50 text-neutral-700';
  }
}

function formatLastSync(date: Date | undefined): string {
  if (!date) return 'No sync recorded';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

function signalCopy(source: SourceReadModel): string {
  const count = source.itemCount ?? 0;
  return `${count} ${count === 1 ? 'signal' : 'signals'}`;
}

function SourceRow({
  source,
  selected,
  onSelect,
}: {
  source: SourceReadModel;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      data-testid={`connection-row-${source.id}`}
      data-source-id={source.id}
      data-display-state={source.displayState}
      className={cn(
        'grid w-full gap-2 rounded-lg border bg-white/80 p-4 text-left shadow-sm transition',
        selected ? 'border-emerald-300 ring-2 ring-emerald-100' : 'border-neutral-200 hover:border-neutral-300',
      )}
      onClick={onSelect}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-medium text-neutral-950">{source.label}</p>
          <p className="mt-1 text-xs text-neutral-500">{source.provider} · {source.type}</p>
        </div>
        <span className={cn('shrink-0 rounded-full border px-2 py-1 text-xs font-medium', stateTone(source))}>
          {statusLabel(source)}
        </span>
      </div>

      <div className="grid gap-1 text-sm text-neutral-600 sm:grid-cols-3">
        <span>{formatLastSync(source.lastSync)}</span>
        <span>{signalCopy(source)}</span>
        <span>{actionCopy(source)}</span>
      </div>

      {(source.authOutcome?.reason || source.error) && (
        <p className="text-xs text-neutral-500">
          {source.authOutcome?.reason ?? source.error}
        </p>
      )}
    </button>
  );
}

export function Connections() {
  const { isLoading, models, error } = useSourceReadModels();
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);

  useEffect(() => {
    if (selectedSourceId && models.some(model => model.id === selectedSourceId)) return;
    setSelectedSourceId(models[0]?.id ?? null);
  }, [models, selectedSourceId]);

  useEffect(() => {
    if (isLoading || error) return;
    recordSlice05ConnectionMarker({
      kind: 'connections_render',
      status: 'success',
      reasonCode: `source_count:${models.length}`,
    });
  }, [error, isLoading, models.length]);

  const selectedSource = useMemo(
    () => models.find(model => model.id === selectedSourceId) ?? models[0],
    [models, selectedSourceId],
  );

  return (
    <div
      data-testid="connections-screen"
      data-source-count={models.length}
      className="mx-auto flex w-full max-w-6xl flex-col gap-4 pb-10"
    >
      <header className="rounded-lg border border-neutral-200 bg-white/80 p-5 shadow-sm">
        <p className="text-xs font-medium uppercase tracking-wide text-emerald-700">Connections</p>
        <h1 className="mt-1 font-serif text-3xl font-semibold text-neutral-950">Sources</h1>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-neutral-600">
          Local source state, sync metadata, and provider recovery status. This surface does not run source sync.
        </p>
      </header>

      {isLoading ? (
        <div data-testid="connections-loading" className="rounded-lg border border-neutral-200 bg-white/70 p-5 text-sm text-neutral-500">
          Loading source read model...
        </div>
      ) : error ? (
        <div data-testid="connections-error" className="rounded-lg border border-red-200 bg-red-50 p-5 text-sm text-red-700">
          {error}
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
          <section
            data-testid="connections-read-model-list"
            data-count={models.length}
            className="grid gap-3"
          >
            {models.map(source => (
              <SourceRow
                key={source.id}
                source={source}
                selected={source.id === selectedSource?.id}
                onSelect={() => setSelectedSourceId(source.id)}
              />
            ))}
          </section>

          {selectedSource && (
            <SourceDetailPanel source={selectedSource} />
          )}
        </div>
      )}
    </div>
  );
}

export default Connections;
